class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this)
    );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");


    // Only prevent default if user is NOT clicking a link for nav bar
    const summary = this.mainDetailsToggle.querySelector("summary");
    summary.addEventListener("click", (e) => {
      if (!e.target.closest("a")) {
        e.preventDefault(); // block native toggle
      }
    });

     // Hover logic for nav bar
    this.addEventListener("mouseenter", () => this.openMenu());
    this.addEventListener("mouseleave", () => this.closeMenu());
  }

    openMenu() {
    if (!this.mainDetailsToggle.hasAttribute("open")) {
      this.mainDetailsToggle.setAttribute("open", "");
      this.onToggle(); // call the same method to size submenu
    }
  }

  closeMenu() {
    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.mainDetailsToggle.removeAttribute("open");
      this.onToggle(); // ensures cleanup
    }
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (this.mainDetailsToggle.open) {
      this.calculateSubmenuWidth();
    }

    if (
      document.documentElement.style.getPropertyValue(
        "--header-bottom-position-desktop"
      ) !== ""
    )
      return;
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  calculateSubmenuWidth() {
    const submenu = this.querySelector(".header__submenu");
    if (!submenu) return;

    // Check if this menu has grandchildren (multi-column layout)
    const hasGrandchildren = submenu.classList.contains("header__submenu--has-grandchildren");
    
    const submenuItems = submenu.querySelectorAll(".submenu-item-wrapper");
    const grandchildItems = submenu.querySelectorAll(
      ".grandchild-item-wrapper"
    );
    const totalItems = submenuItems.length + grandchildItems.length;

    if (totalItems === 0) return;

    let totalWidth;

    // For menus with grandchildren, calculate columns based on menu groups
    if (hasGrandchildren) {
      const menuGroups = submenu.querySelectorAll(".header__menu-group");
      const totalColumns = menuGroups.length || 1;
      
      submenu.style.setProperty("--columns", totalColumns);
      
      // Fixed column width (260px per column including its padding) + container padding (8px each side)
      const columnWidth = 260;
      const containerPadding = 16; // 8px padding on each side of container
      totalWidth = (totalColumns * columnWidth) + containerPadding;

      submenu.style.setProperty("width", `${totalWidth}px`, "important");
    } else {
      // Single column for child-only menus
      // Calculate width based on longest item text (accounting for bold on hover)
      submenu.style.setProperty("--columns", 1);
      
      const menuItems = submenu.querySelectorAll(".header__menu-item");
      let maxWidth = 280; // minimum width
      
      menuItems.forEach(item => {
        // Create a temporary span to measure bold text width
        const tempSpan = document.createElement('span');
        tempSpan.style.cssText = 'position: absolute; visibility: hidden; white-space: nowrap; font-size: 14px; font-weight: 600; font-family: inherit;';
        tempSpan.textContent = item.textContent.trim();
        document.body.appendChild(tempSpan);
        
        const textWidth = tempSpan.offsetWidth + 60; // Add padding (20px each side + buffer)
        if (textWidth > maxWidth) {
          maxWidth = textWidth;
        }
        
        document.body.removeChild(tempSpan);
      });
      
      totalWidth = Math.min(maxWidth, 500); // Cap at 500px max
      submenu.style.setProperty("width", `${totalWidth}px`, "important");
    }

    // Ensure menu stays within viewport
    this.adjustMenuPosition(submenu, totalWidth);
  }

  adjustMenuPosition(submenu, menuWidth) {
    const viewportWidth = window.innerWidth;
    const menuRect = this.getBoundingClientRect();
    const parentLeft = menuRect.left;
    const parentCenter = parentLeft + (menuRect.width / 2);
    const padding = 16; // Padding from viewport edge
    const hasGrandchildren = submenu.classList.contains("header__submenu--has-grandchildren");

    // Reset any previous positioning
    submenu.style.removeProperty("left");
    submenu.style.removeProperty("right");
    submenu.style.removeProperty("transform");

    if (hasGrandchildren) {
      // For multi-column menus, try to center under parent first
      const idealLeft = parentCenter - (menuWidth / 2);
      const menuEndPosition = idealLeft + menuWidth;
      
      // Check if it overflows right
      if (menuEndPosition > viewportWidth - padding) {
        const overflow = menuEndPosition - viewportWidth + padding;
        const adjustedLeft = idealLeft - overflow;
        // Make sure it doesn't go off left side
        const finalLeft = Math.max(padding - parentLeft, adjustedLeft - parentLeft);
        submenu.style.setProperty("left", `${finalLeft}px`, "important");
      } 
      // Check if it overflows left
      else if (idealLeft < padding) {
        const adjustedLeft = padding;
        submenu.style.setProperty("left", `${adjustedLeft - parentLeft}px`, "important");
      } 
      else {
        // Center it
        const centerOffset = idealLeft - parentLeft;
        submenu.style.setProperty("left", `${centerOffset}px`, "important");
      }
    } else {
      // For single-column menus, left-align but keep within viewport
      const menuEndPosition = parentLeft + menuWidth;

      if (menuEndPosition > viewportWidth - padding) {
        const overflow = menuEndPosition - viewportWidth + padding;
        const maxShift = parentLeft - padding;
        const shift = Math.min(overflow, maxShift);
        submenu.style.setProperty("left", `-${shift}px`, "important");
      } else {
        submenu.style.setProperty("left", "0", "important");
      }
    }
  }
}

customElements.define("header-menu", HeaderMenu);
